import { useEffect, useRef, useState } from "react";
import { modelMediaUrl } from "@/lib/api";

const SCRIPT_ID = "google-model-viewer";
const SCRIPT_SRC = "https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js";

function loadModelViewer() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.customElements?.get("model-viewer")) return Promise.resolve();
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return window.customElements.whenDefined("model-viewer");
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.type = "module";
    s.src = SCRIPT_SRC;
    s.onload = () => {
      if (window.customElements?.whenDefined) {
        window.customElements.whenDefined("model-viewer").then(resolve).catch(resolve);
      } else resolve();
    };
    s.onerror = () => reject(new Error("Could not load 3D viewer"));
    document.head.appendChild(s);
  });
}

export default function ProductModelViewer({ src, alt = "3D model" }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  const [loadPct, setLoadPct] = useState(0);
  const url = modelMediaUrl(src);

  useEffect(() => {
    let cancelled = false;
    setLoadPct(0);
    loadModelViewer()
      .then(() => { if (!cancelled) setReady(true); })
      .catch((e) => { if (!cancelled) setErr(e.message || "3D viewer failed to load"); });
    return () => { cancelled = true; };
  }, [url]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !ready || !url) return;
    setLoadPct(0);
    el.setAttribute("src", url);
    el.setAttribute("alt", alt);
    el.setAttribute("camera-controls", "");
    el.setAttribute("touch-action", "pan-y");
    el.setAttribute("auto-rotate", "");
    el.setAttribute("shadow-intensity", "1");
    el.setAttribute("exposure", "1");
    el.style.width = "100%";
    el.style.height = "100%";
    el.style.background = "#f5f5f4";
    const onProgress = (ev) => {
      const t = ev?.detail?.totalProgress;
      if (typeof t === "number") setLoadPct(Math.max(0, Math.min(100, Math.round(t * 100))));
    };
    const onLoad = () => setLoadPct(100);
    const onError = () => setErr("Could not load 3D model");
    el.addEventListener("progress", onProgress);
    el.addEventListener("load", onLoad);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("progress", onProgress);
      el.removeEventListener("load", onLoad);
      el.removeEventListener("error", onError);
    };
  }, [ready, url, alt]);

  if (!url) return null;
  if (err) {
    return <div className="w-full h-full flex items-center justify-center text-sm text-stone-500 p-4 text-center">{err}</div>;
  }
  const showLoader = !ready || loadPct < 100;
  return (
    <div className="relative w-full h-full">
      {ready ? (
        <model-viewer
          ref={ref}
          data-testid="product-3d-viewer"
          style={{ width: "100%", height: "100%", background: "#f5f5f4" }}
        />
      ) : null}
      {showLoader && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-stone-100/90 p-4"
          data-testid="product-3d-loading"
        >
          <div className="text-sm font-semibold text-stone-800" data-testid="product-3d-progress-label">
            Please wait — loading {ready ? loadPct : 0}%
          </div>
          <div className="mt-3 w-40 h-2 rounded-full bg-stone-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-stone-800 transition-all duration-150"
              style={{ width: `${ready ? loadPct : 0}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
